import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Modal,
  Button as BootstrapButton,
} from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTicketAlt,
  FaFilm,
  FaTimes,
  FaExclamationTriangle,
  FaEye,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaChair,
  FaSearch,
  FaBoxOpen,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ModernLoader from "../components/common/ModernLoader";
import MovieTicket from "../components/ticket/MovieTicket";
import { useAuth } from "@clerk/clerk-react";
import moment from "moment";
import "../styles/my-bookings.css";

// Industry-Level Memoized Booking Card to prevent unnecessary re-renders when drawer opens
const BookingCard = React.memo(({ booking, idx, onView, onCancel }) => {
  const isPast = moment(booking.showDate || booking.show?.showDate).isBefore(moment(), 'day');
  const poster = booking.show?.movie?.poster || booking.moviePoster || "https://via.placeholder.com/220x320?text=No+Poster";

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
      <div className="professional-booking-card">
        <div className={`booking-status-tag status-${booking.status}`}>{booking.status}</div>
        <div className="booking-card-main">
          <div className="booking-card-top-row">
            <div className="booking-card-poster-shell">
              <img
                src={poster}
                alt={booking.movieTitle}
                className="booking-card-poster"
              />
            </div>
            <div className="booking-card-info">
              <div className="booking-id-row">#{booking.bookingId?.slice(-10)}</div>
              <h3 className="booking-card-title">{booking.show?.movie?.title || booking.movieTitle}</h3>
            </div>
          </div>
          <div className="booking-details-list">
            <div className="booking-detail-item"><FaMapMarkerAlt /> <span className="text-truncate">{booking.show?.theater?.name || booking.theaterName}</span></div>
            <div className="booking-detail-item"><FaCalendarAlt /> <span>{moment(booking.showDate || booking.show?.showDate).format("MMM DD, YYYY")}</span></div>
            <div className="booking-detail-item"><FaClock /> <span>{booking.showTime || booking.show?.showTime}</span></div>
            <div className="booking-detail-item"><FaChair /> <span>{booking.seats?.length} Tickets</span></div>
          </div>
        </div>
        <div className="booking-card-footer">
          <div className="booking-price-value">₹{booking.totalAmount}</div>
          <div className="booking-actions">
            {booking.status === "confirmed" && (
              <>
                <button className="booking-action-btn btn-view" onClick={() => onView(booking)}><FaEye /> View</button>
                {!isPast && (
                  <button className="booking-action-btn btn-cancel" onClick={() => onCancel(booking)}>Cancel</button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
});

const MyBookings = () => {
  const { getToken, isSignedIn } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState({
    show: false,
    booking: null,
  });
  const [cancelling, setCancelling] = useState(false);
  const [ticketModal, setTicketModal] = useState({
    show: false,
    booking: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterFixed, setIsFilterFixed] = useState(false);
  const filterRef = useRef(null);

  const bookingsPerPage = 9;

  useEffect(() => {
    fetchBookings();
  }, []);

  // Industry-Standard Scroll Lock
  useEffect(() => {
    if (ticketModal.show || cancelModal.show) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflowY = "scroll"; // Keep scrollbar to prevent layout shift
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflowY = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflowY = "";
    };
  }, [ticketModal.show, cancelModal.show]);

  useEffect(() => {
    const onScroll = () => {
      if (!filterRef.current) return;
      const rect = filterRef.current.getBoundingClientRect();
      // The navbar height is usually 98px. We stick when the container's top hits that.
      const threshold = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--district-header-height')) || 98;

      if (rect.top <= threshold) {
        setIsFilterFixed(true);
      } else {
        setIsFilterFixed(false);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const fetchBookings = async () => {
    try {
      if (!isSignedIn) {
        setLoading(false);
        return;
      }
      const token = await getToken();
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const response = await fetch(`${apiUrl}/bookings/my-bookings`, {
        headers: { Authorization: `Bearer ${token}`, "Cache-Control": "no-cache" },
      });
      if (!response.ok) throw new Error("Failed to fetch bookings");
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const getMoviePoster = (booking) => {
    return booking.show?.movie?.poster || booking.moviePoster || "https://via.placeholder.com/220x320?text=No+Poster";
  };

  const filteredBookings = useMemo(() => {
    let result = bookings;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b =>
        (b.show?.movie?.title || b.movieTitle || "").toLowerCase().includes(q) ||
        (b.bookingId || "").toLowerCase().includes(q)
      );
    }
    if (activeFilter !== "All") {
      result = result.filter(booking => {
        const isPast = moment(booking.showDate || booking.show?.showDate).isBefore(moment(), 'day');
        const isConfirmed = booking.status === "confirmed";
        const isCancelled = booking.status === "cancelled";
        if (activeFilter === "Active") return isConfirmed && !isPast;
        if (activeFilter === "Past") return isPast || isCancelled;
        if (activeFilter === "Cancelled") return isCancelled;
        return true;
      });
    }
    return result;
  }, [bookings, activeFilter, searchQuery]);

  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);
  const startIdx = (currentPage - 1) * bookingsPerPage;
  const currentBookingsSubset = filteredBookings.slice(startIdx, startIdx + bookingsPerPage);

  // Industry-level pagination logic (Ellipses support)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage > totalPages - 3) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const handleCancelBooking = useCallback(async () => {
    if (!cancelModal.booking) return;
    setCancelling(true);
    try {
      const token = await getToken();
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const response = await fetch(`${apiUrl}/bookings/${cancelModal.booking._id}/cancel`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to cancel");
      setBookings(prev => prev.map(b =>
        b._id === cancelModal.booking._id ? { ...b, status: "cancelled", cancelledAt: new Date() } : b
      ));
      toast.success("Booking Cancelled");
      setCancelModal({ show: false, booking: null });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCancelling(false);
    }
  }, [cancelModal.booking, getToken]);

  const openTicket = useCallback((b) => setTicketModal({ show: true, booking: b }), []);
  const openCancel = useCallback((b) => setCancelModal({ show: true, booking: b }), []);

  const memoizedBookingCards = useMemo(() => (
    currentBookingsSubset.map((booking, idx) => (
      <BookingCard
        key={booking._id}
        booking={booking}
        idx={idx}
        onView={openTicket}
        onCancel={openCancel}
      />
    ))
  ), [currentBookingsSubset, openTicket, openCancel]);

  if (loading) return <ModernLoader text="Your Tickets" />;

  return (
    <div className="my-bookings-container">
      <div className="district-bookings-shell">

        <header className="district-bookings-header-section">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <h1>My Bookings</h1>
            <p>View tickets and manage your movie history in one place</p>
          </motion.div>
        </header>

        <div ref={filterRef} className="district-bookings-filter-container">
          <div className={`district-bookings-filter-bar ${isFilterFixed ? "is-fixed" : ""}`}>
            <div className="filter-bar-inner">
              <div className="booking-search-wrap">
                <FaSearch className="search-icon" />
                <input
                  type="text" placeholder="Search movies or booking ID..."
                  value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <div className="district-bookings-filters">
                {["All", "Active", "Past", "Cancelled"].map((f) => (
                  <button key={f} className={`booking-filter-chip ${activeFilter === f ? "active" : ""}`} onClick={() => { setActiveFilter(f); setCurrentPage(1); }}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {filteredBookings.length > 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

              <div className="results-summary">
                Showing <span>{startIdx + 1}-{Math.min(startIdx + bookingsPerPage, filteredBookings.length)}</span> of <strong>{filteredBookings.length}</strong> bookings
              </div>

              <div className="district-bookings-grid">
                {memoizedBookingCards}
              </div>

              {/* Advanced Real-World Pagination */}
              {totalPages > 1 && (
                <div className="professional-pagination-wrapper">
                  <button className="pagination-arrow-btn" disabled={currentPage === 1} onClick={() => { setCurrentPage(prev => prev - 1); window.scrollTo({ top: 400, behavior: 'smooth' }); }}>
                    <FaChevronLeft /> <span>Prev</span>
                  </button>
                  <div className="pagination-numbers-track">
                    {getPageNumbers().map((num, i) => (
                      num === '...' ? <span key={`dots-${i}`} className="pagination-dots">...</span> :
                        <button key={`page-${num}`} className={`pagination-number-pill ${currentPage === num ? 'is-active' : ''}`} onClick={() => { setCurrentPage(num); window.scrollTo({ top: 400, behavior: 'smooth' }); }}>{num}</button>
                    ))}
                  </div>
                  <button className="pagination-arrow-btn" disabled={currentPage === totalPages} onClick={() => { setCurrentPage(prev => prev + 1); window.scrollTo({ top: 400, behavior: 'smooth' }); }}>
                    <span>Next</span> <FaChevronRight />
                  </button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="bookings-empty-state">
              <div className="empty-icon-wrap"><FaBoxOpen size={48} className="opacity-50" /></div>
              <h2 className="empty-state-title">No bookings match your view</h2>
              <p className="empty-state-desc">{searchQuery ? `We searched for "${searchQuery}" but found nothing.` : "Time to browse some movies and fill this list!"}</p>
              {!searchQuery && <a href="/movies" className="browse-movies-btn">Explore Movies</a>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Modal
        show={cancelModal.show}
        onHide={() => setCancelModal({ show: false, booking: null })}
        centered
        contentClassName="premium-cancel-modal"
      >
        <div className="cancel-modal-inner">
          <div className="cancel-icon-ring">
            <FaExclamationTriangle size={24} />
          </div>

          <div className="cancel-content-box">
            <h2 className="cancel-title">Cancel Adventure?</h2>
            <p className="cancel-desc">Are you sure you want to cancel your tickets for <strong>{cancelModal.booking?.movieTitle}</strong>?</p>

            {cancelModal.booking && (
              <div className="refund-summary-card">
                <div className="refund-row">
                  <span>Booking Amount</span>
                  <span>₹{cancelModal.booking.totalAmount}</span>
                </div>
                <div className="refund-row">
                  <span>Convenience Fee (10%)</span>
                  <span>- ₹{Math.round(cancelModal.booking.totalAmount * 0.1)}</span>
                </div>
                <div className="refund-divider" />
                <div className="refund-row total">
                  <span>Estimated Refund</span>
                  <span className="total-value">₹{Math.round(cancelModal.booking.totalAmount * 0.9)}</span>
                </div>
              </div>
            )}

            <p className="refund-policy-notice">* Refund will be credited to original payment method within 5-7 business days.</p>
          </div>

          <div className="cancel-modal-actions">
            <button className="cancel-secondary-btn" onClick={() => setCancelModal({ show: false, booking: null })}>Keep Booking</button>
            <button className="cancel-primary-btn" onClick={handleCancelBooking} disabled={cancelling}>
              {cancelling ? (
                <span className="btn-loader">Processing...</span>
              ) : (
                "Confirm Cancellation"
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Industry Level Cinematic Right-Side Drawer */}
      {createPortal(
        <AnimatePresence>
          {ticketModal.show && ticketModal.booking && (
            <div className="premium-drawer-wrapper">
              <motion.div
                className="drawer-glass-backdrop"
                onClick={() => setTicketModal({ show: false, booking: null })}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              <motion.div
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{
                  type: "spring",
                  damping: 30,
                  stiffness: 250,
                  mass: 0.8
                }}
                className="premium-right-drawer"
              >
                <div className="drawer-header">
                  <div className="drawer-title-wrap">
                    <h2>Digital Ticket</h2>
                    <p>Order ID: #{ticketModal.booking.bookingId?.slice(-8).toUpperCase()}</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    className="drawer-close-btn"
                    onClick={() => setTicketModal({ show: false, booking: null })}
                  >
                    <FaTimes />
                  </motion.button>
                </div>

                <div className="drawer-body-scroll" data-lenis-prevent>
                  <div className="drawer-ticket-host">
                    <MovieTicket booking={ticketModal.booking} />
                  </div>

                  <div className="drawer-footer-info">
                    <div className="info-badge">
                      <FaTicketAlt /> Valid for entry
                    </div>
                    <p className="footer-notice">
                      Present this QR at the cinema gate for scanning.
                      Screenshots are valid for entry.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default MyBookings;
